import { useCallback, useEffect } from "react";
import { LiveSessionError, saveLiveSessions } from "../live/liveHelper";
import { VerityApi } from "../../api/fetch";
import { convertPartialOptionsToNonNullable, equalProctoringOptions } from "@verity/proctoring-option";
import { EditQuizPageProps, EditQuizRef } from "../editQuiz";
import { UseEditQuizStateReturn } from "./state/useEditQuizState";
import { isThirdPartyStepValid, isValidPlatform } from "../editQuizHelper";
import i18nPackage from "../../i18n/i18n";

type UseEditQuizSubmitProps = Pick<
	EditQuizPageProps,
	| "quizInfo"
	| "logger"
	| "proctoringAssessmentConfiguration"
	| "proctoringOption"
	| "setCurrentProfileId"
	| "currentProfileId"
	| "onSaveOptions"
	| "onStartSaveOptions"
	| "onSaveEnabledChange"
	| "enforcedProfilesOnly"
	| "onClose"
	| "quizType"
> &
	Pick<UseEditQuizStateReturn, "profile" | "student" | "theme" | "live" | "setting" | "submit"> & {
		api: VerityApi;
	};

/**
 * Handles logic related to saving the quiz AND the close button is here too because not sure where to put it
 * originally in editQuiz.ts
 */
export default function useEditQuizSubmit({
	quizInfo,
	proctoringAssessmentConfiguration,
	proctoringOption,
	logger,
	onSaveOptions,
	onStartSaveOptions,
	api,
	setCurrentProfileId,
	currentProfileId,
	onSaveEnabledChange,
	enforcedProfilesOnly,
	onClose,
	quizType,
	...props
}: UseEditQuizSubmitProps) {
	const { addAssessmentId } = props.setting.action;
	const { assessmentUuids, verityInstituteSettings, proctoringData } = props.setting.state;
	const { isUpdateCurrentProfileClicked, activeProfile } = props.profile.state;
	const { isExemptedStudentsModified } = props.student.state;
	const { handleSaveExemptedStudentsNotStandalone } = props.student.action;
	const { themeExists } = props.theme.state;
	const { validateLiveSession, fetchAndSetExistingLiveSessions } = props.live.action;
	const { existingLiveSessions, pendingLiveSessions, pendingLiveSessionIds } = props.live.state;
	const {
		resetSaveStates,
		completePresave,
		saveProctoringConfiguration,
		confirmSave,
		confirmClose,
		openDisclaimerModal,
		initiateSaveProcess,
	} = props.submit.action;
	const {
		callReset,
		saveParameters,
		savePromise,
		finishedPresave,
		saveRequested,
		isAnyProctoringOptionEnabled,
		isAssessmentConfigurationModified,
		isProctoringOptionModified,
	} = props.submit.state;

	useEffect(() => {
		if (callReset) {
			resetSaveStates();
		}
	}, [callReset, resetSaveStates]);

	const saveLiveSessionsHandleErrors = useCallback(async () => {
		if (assessmentUuids.current) {
			try {
				const assessmentUuidsResult = await addAssessmentId(assessmentUuids.current);
				if (!assessmentUuidsResult) {
					throw new Error("No assessment UUIDs");
				}

				await saveLiveSessions({
					api,
					assessmentUuids: assessmentUuidsResult,
					existingSessions: existingLiveSessions,
					pendingSessions: pendingLiveSessions,
				});
			} finally {
				// update existing sessions for diff calculation
				await fetchAndSetExistingLiveSessions(assessmentUuids.current);
			}
		} else if (pendingLiveSessionIds.length > 0) {
			throw new Error("No assessment UUIDs");
		}
	}, [
		addAssessmentId,
		api,
		assessmentUuids,
		existingLiveSessions,
		fetchAndSetExistingLiveSessions,
		pendingLiveSessionIds.length,
		pendingLiveSessions,
	]);

	const defaultSave = useCallback(async () => {
		const currentQuizId = quizInfo.quizId;
		if (!currentQuizId) {
			onStartSaveOptions?.({
				...proctoringOption,
				profileId: activeProfile?.id ?? currentProfileId,
				pendingLiveSessions,
			});
			onSaveOptions(true, undefined);
			return;
		}

		/* Define the functions error handler */
		const logError = (e: unknown) => {
			console.error("Failed to save verity option ", e);
			void logger?.error(e);
			if (e instanceof LiveSessionError) {
				onSaveOptions(false, e.messages);
			} else {
				onSaveOptions(false, [i18nPackage.t("editQuizPage.failToSave")]);
			}
			resetSaveStates();
		};

		/* Destructure passed parameters */
		const { password, additionalAssessmentInfo } = saveParameters;

		// if options are different from profile, prompt choice between option or profile
		if (
			proctoringOption &&
			activeProfile &&
			!equalProctoringOptions(convertPartialOptionsToNonNullable(proctoringOption), activeProfile.proctoringOption)
		) {
			onStartSaveOptions?.();
			if (!isUpdateCurrentProfileClicked) {
				setCurrentProfileId(undefined);
			}
			try {
				const savedOptions = await saveProctoringConfiguration(
					password,
					additionalAssessmentInfo,
					false,
					!isUpdateCurrentProfileClicked ? true : false,
				);
				await saveLiveSessionsHandleErrors();
				void logger?.assessment.edit.configurationModal.saveFinished(true);
				onSaveOptions(true, undefined, savedOptions);
			} catch (e) {
				logError(e);
			}
		} else {
			onStartSaveOptions?.();
			try {
				const savedOptions = await saveProctoringConfiguration(password, additionalAssessmentInfo);
				await handleSaveExemptedStudentsNotStandalone(currentQuizId);
				await saveLiveSessionsHandleErrors();
				void logger?.assessment.edit.configurationModal.saveFinished(true);
				onSaveOptions(true, undefined, savedOptions);
			} catch (e) {
				logError(e);
			}
		}

		/* Resolve the promise passed back to parents */
		savePromise.resolve();
	}, [
		activeProfile,
		currentProfileId,
		handleSaveExemptedStudentsNotStandalone,
		isUpdateCurrentProfileClicked,
		logger,
		onSaveOptions,
		onStartSaveOptions,
		pendingLiveSessions,
		proctoringOption,
		quizInfo.quizId,
		resetSaveStates,
		saveLiveSessionsHandleErrors,
		saveParameters,
		saveProctoringConfiguration,
		savePromise,
		setCurrentProfileId,
	]);

	/* Save Request Routing */
	useEffect(() => {
		/* If save has been requested */
		if (saveRequested) {
			/* Initiate save if presave steps are complete */
			if (finishedPresave) {
				void defaultSave();
			} else {
				/* Otherwise, initiate presave steps */
				switch (quizInfo.lms) {
					case "canvas":
						if (
							!!proctoringAssessmentConfiguration?.thirdPartyAssessmentOption ||
							quizType === "standalone" ||
							themeExists
						) {
							completePresave();
						} else {
							openDisclaimerModal();
						}
						break;
					default:
						completePresave();
						break;
				}
			}
		}
	}, [saveRequested, finishedPresave]);

	/**
	 * This function is awaited by 2 parent wrappers on clicking submit
	 * The parents upon click of submit also execute other internal logic
	 *
	 * Hence, we must perform any logic that is to be executed on save request
	 * while halting the parent call
	 *
	 * Manually create and return a promise to the parent on call, and
	 * resolve / reject upon completion
	 *
	 */
	const saveButtonHandler: EditQuizRef["saveButtonHandler"] = useCallback(
		async (password, additionalAssessmentInfo) => {
			// Since progress bar is non linear, it is possible for user to create a live session, click back button (skipping the validation), then jump to confirmation step.
			// Intentionally did not add validation check to back button because if user wants to switch method to automated in step 1,
			// they must delete a live session in step 2, but if validation blocks them from leaving step 2 cuz they didn't create a live session, it creates an infinite loop
			if (proctoringAssessmentConfiguration?.isLiveProctored && !validateLiveSession()) return false;

			if (!isAnyProctoringOptionEnabled) {
				const userConfirmed = await confirmSave();
				if (!userConfirmed) {
					return false;
				}
			}
			initiateSaveProcess({ additionalAssessmentInfo, password });
			return true;
		},
		[
			proctoringAssessmentConfiguration?.isLiveProctored,
			validateLiveSession,
			isAnyProctoringOptionEnabled,
			initiateSaveProcess,
			confirmSave,
		],
	);

	const closeButtonHandler: EditQuizRef["closeButtonHandler"] = useCallback(async () => {
		if (isAssessmentConfigurationModified || isProctoringOptionModified || isExemptedStudentsModified) {
			const userConfirmed = await confirmClose();
			if (!userConfirmed) {
				return false;
			}
		}

		onClose?.();
		return true;
	}, [
		confirmClose,
		isAssessmentConfigurationModified,
		isExemptedStudentsModified,
		isProctoringOptionModified,
		onClose,
	]);

	// Handles validation of save button
	useEffect(() => {
		// Live session not okay if user chose live session option but did not create a live session
		const liveSessionOk = !proctoringAssessmentConfiguration?.isLiveProctored || pendingLiveSessionIds.length > 0;
		const thirdPartyOk = isThirdPartyStepValid(proctoringData.thirdPartyPlatforms, proctoringAssessmentConfiguration);

		const saveEnabled =
			liveSessionOk &&
			thirdPartyOk &&
			(!enforcedProfilesOnly || Boolean(activeProfile?.id)) &&
			isValidPlatform({
				option: proctoringOption,
				verityInstituteSettings: verityInstituteSettings,
			});

		onSaveEnabledChange?.({ enabled: saveEnabled });
	}, [
		onSaveEnabledChange,
		enforcedProfilesOnly,
		proctoringOption,
		verityInstituteSettings,
		proctoringAssessmentConfiguration?.isLiveProctored,
		pendingLiveSessionIds.length,
		activeProfile,
		proctoringAssessmentConfiguration,
		proctoringData.thirdPartyPlatforms,
	]);

	return {
		saveButtonHandler,
		closeButtonHandler,
	};
}
